/**
 * OFFICER MANAGEMENT COMPONENT
 *
 * Allows users to add, edit, and remove company officers.
 * Supports pending officer invitations.
 */

import React, { useState } from 'react';
import { CompanyAccountService } from '../../lib/services/companyAccountService';
import { sendOfficerInvitationEmail } from '../../lib/services/companyEmailService';
import type { CompanyOfficer, CompanyOfficerRole } from '../../types/company';

interface OfficerManagementProps {
  companyId: string;
  companyName: string; // Added for email notifications
  officers: CompanyOfficer[];
  onOfficerAdded: () => void;
  onClose: () => void;
}

// Role options matching CompanyOfficerRole type exactly
const roleOptions: { value: CompanyOfficerRole; label: string }[] = [
  { value: 'ceo', label: 'CEO' },
  { value: 'cfo', label: 'CFO' },
  { value: 'cto', label: 'CTO' },
  { value: 'coo', label: 'COO' },
  { value: 'chairman', label: 'Chairman' },
  { value: 'director', label: 'Director' },
  { value: 'non_executive_director', label: 'Non-Executive Director' },
  { value: 'company_secretary', label: 'Company Secretary' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'shareholder_representative', label: 'Shareholder Representative' },
  { value: 'authorized_signatory', label: 'Authorized Signatory' },
  { value: 'custom', label: 'Custom Role' },
];

export const OfficerManagement: React.FC<OfficerManagementProps> = ({
  companyId, companyName, officers, onOfficerAdded, onClose
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    role: 'director' as CompanyOfficerRole, customRoleTitle: '', shareholdingPercentage: 0
  });

  // Edit state
  const [editingOfficer, setEditingOfficer] = useState<CompanyOfficer | null>(null);
  const [editFormData, setEditFormData] = useState({
    role: 'director' as CompanyOfficerRole,
    customRoleTitle: '',
    businessEmail: '',
    businessPhone: '',
    shareholdingPercentage: 0,
    hasSigningAuthority: false
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setError('First name, last name, and email are required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const result = await CompanyAccountService.addPendingOfficer({
        companyId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || undefined,
        role: formData.role,
        customRoleTitle: formData.customRoleTitle || undefined,
        shareholdingPercentage: formData.shareholdingPercentage || undefined
      });

      if (result.success) {
        // Send officer invitation email
        const roleLabel = formData.customRoleTitle || roleOptions.find(r => r.value === formData.role)?.label || formData.role;
        sendOfficerInvitationEmail({
          companyName: companyName,
          registrationNumber: '', // Not needed for invitation
          directorName: `${formData.firstName} ${formData.lastName}`,
          directorEmail: formData.email,
          officerName: `${formData.firstName} ${formData.lastName}`,
          officerRole: roleLabel,
          invitationLink: `${window.location.origin}/dashboard`
        }).catch(err => console.error('Failed to send officer invitation email:', err));

        alert('Officer added successfully! They will receive an invitation email.');
        setFormData({ firstName: '', lastName: '', email: '', phone: '', role: 'director', customRoleTitle: '', shareholdingPercentage: 0 });
        setShowAddForm(false);
        onOfficerAdded();
      } else {
        setError(result.error || 'Failed to add officer');
      }
    } catch (err) {
      setError('An error occurred while adding the officer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveOfficer = async (officerId: string) => {
    if (!confirm('Are you sure you want to remove this officer?')) return;

    try {
      const result = await CompanyAccountService.removeOfficer(officerId, new Date().toISOString().split('T')[0]);
      if (result.success) {
        alert('Officer removed successfully');
        onOfficerAdded();
      } else {
        alert(result.error || 'Failed to remove officer');
      }
    } catch (err) {
      alert('An error occurred');
    }
  };

  // Start editing an officer
  const handleStartEdit = (officer: CompanyOfficer) => {
    setEditingOfficer(officer);
    setEditFormData({
      role: officer.role as CompanyOfficerRole,
      customRoleTitle: officer.customRoleTitle || '',
      businessEmail: officer.businessEmail || '',
      businessPhone: officer.businessPhone || '',
      shareholdingPercentage: officer.shareholdingPercentage || 0,
      hasSigningAuthority: officer.hasSigningAuthority || false
    });
    setShowAddForm(false);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingOfficer(null);
    setError(null);
  };

  // Save officer edits
  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOfficer) return;

    setSubmitting(true);
    setError(null);

    try {
      const result = await CompanyAccountService.updateOfficer(editingOfficer.id, {
        role: editFormData.role,
        customRoleTitle: editFormData.role === 'custom' ? editFormData.customRoleTitle : undefined,
        businessEmail: editFormData.businessEmail || undefined,
        businessPhone: editFormData.businessPhone || undefined,
        shareholdingPercentage: editFormData.shareholdingPercentage,
        hasSigningAuthority: editFormData.hasSigningAuthority
      });

      if (result.success) {
        alert('Officer updated successfully!');
        setEditingOfficer(null);
        onOfficerAdded();
      } else {
        setError(result.error || 'Failed to update officer');
      }
    } catch (err) {
      setError('An error occurred while updating the officer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-900 rounded-xl max-w-2xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 px-6 py-4 border-b border-gray-700 flex items-center justify-between z-10">
          <h3 className="text-xl font-bold text-white">👥 Manage Officers</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
        </div>

        <div className="p-6">
          {/* Add Officer Button */}
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full mb-6 px-4 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              ➕ Add New Officer
            </button>
          )}

          {/* Add Officer Form */}
          {showAddForm && (
            <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-800 rounded-lg space-y-4">
              <h4 className="text-white font-medium">Add New Officer</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white mb-1">First Name *</label>
                  <input type="text" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" required />
                </div>
                <div>
                  <label className="block text-sm text-white mb-1">Last Name *</label>
                  <input type="text" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white mb-1">Email *</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" required />
                </div>
                <div>
                  <label className="block text-sm text-white mb-1">Phone</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white mb-1">Role *</label>
                  <select value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value as CompanyOfficerRole})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white">
                    {roleOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-white mb-1">Shareholding %</label>
                  <input type="number" min="0" max="100" step="0.01" value={formData.shareholdingPercentage}
                    onChange={(e) => setFormData({...formData, shareholdingPercentage: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
                </div>
              </div>
              {formData.role === 'custom' && (
                <div>
                  <label className="block text-sm text-white mb-1">Custom Role Title</label>
                  <input type="text" value={formData.customRoleTitle} onChange={(e) => setFormData({...formData, customRoleTitle: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" placeholder="e.g., Financial Controller" />
                </div>
              )}
              {error && <div className="p-3 bg-red-900/30 border border-red-500 rounded-lg text-red-400 text-sm">⚠️ {error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAddForm(false)} disabled={submitting}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg transition-colors disabled:opacity-50">
                  {submitting ? '⏳ Adding...' : '➕ Add Officer'}
                </button>
              </div>
            </form>
          )}

          {/* Edit Officer Form */}
          {editingOfficer && (
            <form onSubmit={handleSaveEdit} className="mb-6 p-4 bg-blue-900/20 border border-blue-700 rounded-lg space-y-4">
              <h4 className="text-white font-medium">✏️ Edit Officer: {(editingOfficer as any).firstName} {(editingOfficer as any).lastName}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white mb-1">Role</label>
                  <select value={editFormData.role} onChange={(e) => setEditFormData({...editFormData, role: e.target.value as CompanyOfficerRole})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white">
                    {roleOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-white mb-1">Shareholding %</label>
                  <input type="number" min="0" max="100" step="0.01" value={editFormData.shareholdingPercentage}
                    onChange={(e) => setEditFormData({...editFormData, shareholdingPercentage: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
                </div>
              </div>
              {editFormData.role === 'custom' && (
                <div>
                  <label className="block text-sm text-white mb-1">Custom Role Title</label>
                  <input type="text" value={editFormData.customRoleTitle} onChange={(e) => setEditFormData({...editFormData, customRoleTitle: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" placeholder="e.g., Financial Controller" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white mb-1">Business Email</label>
                  <input type="email" value={editFormData.businessEmail} onChange={(e) => setEditFormData({...editFormData, businessEmail: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
                </div>
                <div>
                  <label className="block text-sm text-white mb-1">Business Phone</label>
                  <input type="tel" value={editFormData.businessPhone} onChange={(e) => setEditFormData({...editFormData, businessPhone: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="signingAuth" checked={editFormData.hasSigningAuthority}
                  onChange={(e) => setEditFormData({...editFormData, hasSigningAuthority: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-yellow-500 focus:ring-yellow-500" />
                <label htmlFor="signingAuth" className="text-sm text-white">Has Signing Authority</label>
              </div>
              {error && <div className="p-3 bg-red-900/30 border border-red-500 rounded-lg text-red-400 text-sm">⚠️ {error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={handleCancelEdit} disabled={submitting}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-400 text-white font-medium rounded-lg transition-colors disabled:opacity-50">
                  {submitting ? '⏳ Saving...' : '💾 Save Changes'}
                </button>
              </div>
            </form>
          )}

          {/* Officers List */}
          <div className="space-y-3">
            <h4 className="text-white font-medium">Current Officers ({officers.length})</h4>
            {officers.length > 0 ? (
              officers.map((officer) => (
                <div key={officer.id} className={`bg-gray-800 rounded-lg p-4 flex items-center justify-between ${editingOfficer?.id === officer.id ? 'ring-2 ring-blue-500' : ''}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center text-xl">👤</div>
                    <div>
                      <p className="text-white font-medium">{(officer as any).firstName} {(officer as any).lastName}</p>
                      <p className="text-sm text-white">{officer.customRoleTitle || officer.role}</p>
                      {officer.businessEmail && <p className="text-sm text-gray-500">{officer.businessEmail}</p>}
                      {officer.shareholdingPercentage > 0 && <span className="text-sm text-yellow-400">{officer.shareholdingPercentage}% shares</span>}
                      {officer.hasSigningAuthority && <span className="ml-2 text-sm text-blue-400">✍️ Signatory</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {officer.kycVerified ? (
                      <span className="px-2 py-1 bg-green-900/30 text-green-400 text-sm rounded-full">✓ KYC Verified</span>
                    ) : (
                      <span className="px-2 py-1 bg-yellow-900/30 text-yellow-400 text-sm rounded-full">Pending KYC</span>
                    )}
                    {officer.customRoleTitle !== 'Primary Director' && (
                      <>
                        <button onClick={() => handleStartEdit(officer)} className="text-blue-400 hover:text-blue-300 text-sm">✏️ Edit</button>
                        <button onClick={() => handleRemoveOfficer(officer.id)} className="text-red-400 hover:text-red-300 text-sm">🗑️ Remove</button>
                      </>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-white">
                <span className="text-4xl mb-3 block">👥</span>
                <p>No officers on record</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfficerManagement;

